import { useEffect, useMemo, useRef, useState } from "react"
import {
	IconChevronDownOutline18 as ChevronDown,
	IconLanguageOutline18 as Languages,
} from "nucleo-ui-outline-18"

import { pro } from "@productlane/lib"
import { cn } from "@productlane/lib/src/cn"
import {
	Button,
	DropdownMenu,
	DropdownMenuItem,
	Select,
	Tooltip,
} from "@productlane/ui"
import { buttonVariants } from "@productlane/ui/src/components/button"

import { useRequiredPlans } from "@/lib/plans"
import { useZeroWorkspace } from "@/lib/zero"

export interface Language {
	code: string
	name: string
}

export interface LanguageSelectorProps {
	languages?: string[]
	selectedLanguage: Language
	onLanguageSelect: (language: Language) => void
	placeholder?: string
	disabled?: boolean
	showPlaceholder?: boolean
	variant?: "full" | "code-only"
	defaultOpen?: boolean
	emptyLanguageCodes?: string[]
	// Render the trigger as a plain Select (settings rows) instead of the
	// outline-button look used in editor toolbars.
	plainSelectStyle?: boolean
}

export const LANGUAGES: Language[] = [
	{ code: "en", name: "English" },
	{ code: "bg", name: "Bulgarian" },
	{ code: "de", name: "German" },
	{ code: "de-formal", name: "German (formal)" },
	{ code: "fr", name: "French" },
	{ code: "es", name: "Spanish" },
	{ code: "it", name: "Italian" },
	{ code: "pt", name: "Portuguese" },
	{ code: "pt-BR", name: "Portuguese (Brazil)" },
	{ code: "nl", name: "Dutch" },
	{ code: "pl", name: "Polish" },
	{ code: "ja", name: "Japanese" },
	{ code: "ko", name: "Korean" },
	{ code: "zh", name: "Chinese" },
	{ code: "zh-CN", name: "Chinese (Simplified)" },
	{ code: "zh-TW", name: "Chinese (Traditional)" },
	{ code: "tr", name: "Turkish" },
	{ code: "ar", name: "Arabic" },
	{ code: "hi", name: "Hindi" },
	{ code: "bn", name: "Bengali" },
	{ code: "da", name: "Danish" },
	{ code: "fi", name: "Finnish" },
	{ code: "no", name: "Norwegian" },
	{ code: "sv", name: "Swedish" },
	{ code: "cs", name: "Czech" },
	{ code: "el", name: "Greek" },
	{ code: "et", name: "Estonian" },
	{ code: "hr", name: "Croatian" },
	{ code: "hu", name: "Hungarian" },
	{ code: "ro", name: "Romanian" },
	{ code: "th", name: "Thai" },
	{ code: "uk", name: "Ukrainian" },
	{ code: "vi", name: "Vietnamese" },
	{ code: "he", name: "Hebrew" },
	{ code: "fa", name: "Persian" },
	{ code: "sw", name: "Swahili" },
	{ code: "bs", name: "Bosnian" },
	{ code: "ca", name: "Catalan" },
	{ code: "ms", name: "Malay" },
	{ code: "id", name: "Indonesian" },
	{ code: "lt", name: "Lithuanian" },
	{ code: "lv", name: "Latvian" },
	{ code: "mn", name: "Mongolian" },
	{ code: "kk", name: "Kazakh" },
	{ code: "ru", name: "Russian" },
	{ code: "sl", name: "Slovenian" },
	{ code: "sr", name: "Serbian" },
]

export const EN_LANGUAGE = LANGUAGES[0]!

export const getLanguageByCode = (code: string) => {
	return LANGUAGES.find((lang) => lang.code === code)
}

export const getLanguageByCodeWithFallback = (code?: string | null) => {
	return (code && getLanguageByCode(code)) || EN_LANGUAGE
}

export const useSelectedLanguage = (): [
	Language,
	(language: Language) => void,
] => {
	const workspace = useZeroWorkspace()
	const [selectedLanguage, setSelectedLanguage] = useState<Language>(
		getLanguageByCode(window.localStorage.getItem("selectedLanguage") || "") ||
			getLanguageByCodeWithFallback(workspace?.defaultLanguage),
	)

	useEffect(() => {
		if (!workspace) return
		if (!workspace?.enabledLanguages?.includes(selectedLanguage.code)) {
			setSelectedLanguage(
				getLanguageByCodeWithFallback(workspace?.defaultLanguage),
			)
		}
	}, [
		workspace,
		workspace?.enabledLanguages,
		selectedLanguage.code,
		workspace?.defaultLanguage,
	])

	useEffect(() => {
		window.localStorage.setItem("selectedLanguage", selectedLanguage.code)
	}, [selectedLanguage.code])

	return [selectedLanguage, setSelectedLanguage]
}

export function LanguageSelector({
	languages,
	selectedLanguage,
	onLanguageSelect,
	placeholder = "Select a language...",
	disabled = false,
	showPlaceholder = false,
	variant = "full",
	defaultOpen = false,
	emptyLanguageCodes,
	plainSelectStyle = false,
}: LanguageSelectorProps) {
	const selectRef = useRef<HTMLButtonElement>(null)
	const { passes: hasRequiredPlan } = useRequiredPlans(pro.andUp)

	const languageObjects = useMemo(() => {
		if (!languages) return LANGUAGES
		return languages.map(getLanguageByCode).filter((l) => l !== undefined)
	}, [languages])

	const options = useMemo(() => {
		return languageObjects.map((lang) => {
			const isEmpty = emptyLanguageCodes?.includes(lang.code)
			return {
				value: lang.code,
				label: isEmpty ? (
					<span className="flex items-center gap-2">
						{lang.name}
						<Tooltip label="Translation is empty">
							<button
								type="button"
								className="h-1.5 w-1.5 rounded-full bg-amber-500"
								aria-label="Translation is empty"
							/>
						</Tooltip>
					</span>
				) : (
					lang.name
				),
				triggerLabel:
					variant === "code-only" ? lang.code.toUpperCase() : lang.name,
			}
		})
	}, [languageObjects, variant, emptyLanguageCodes])

	const handleSelect = (languageCode: string) => {
		const language = languageObjects.find((lang) => lang.code === languageCode)
		if (language) {
			onLanguageSelect(language)
		}
		setTimeout(() => {
			selectRef.current?.blur()
		}, 0)
	}

	const displayValue = useMemo(() => {
		if (showPlaceholder && !selectedLanguage.code) {
			return ""
		}
		return selectedLanguage.code
	}, [selectedLanguage.code, showPlaceholder])

	useEffect(() => {
		if (defaultOpen && selectRef.current) {
			setTimeout(() => {
				selectRef.current?.click()
			}, 0)
		}
	}, [defaultOpen])

	if (!hasRequiredPlan) {
		return null
	}

	const isSelectedEmpty = emptyLanguageCodes?.includes(selectedLanguage.code)

	if (variant === "code-only") {
		return (
			<Tooltip
				label="Translation is empty"
				enabled={!!isSelectedEmpty}
				shouldWrapChildren
				side="right"
			>
				<DropdownMenu
					defaultOpen={defaultOpen}
					trigger={
						<Button
							Icon={Languages}
							iconClassName={cn("h-3.5 w-3.5 text-green-500", {
								"text-amber-500": isSelectedEmpty,
							})}
							variant="outline"
							disabled={disabled}
						>
							<span className="flex items-center gap-1">
								{selectedLanguage.code.toUpperCase()}
								<ChevronDown className="h-3 w-3 opacity-50" />
							</span>
						</Button>
					}
					contentProps={{ side: "bottom", align: "end" }}
				>
					{languageObjects.map((lang) => {
						const isEmpty = emptyLanguageCodes?.includes(lang.code)
						const isSelected = lang.code === selectedLanguage.code
						return (
							<DropdownMenuItem
								key={lang.code}
								onClick={() => onLanguageSelect(lang)}
								className={cn(isSelected && "bg-gray-100 dark:bg-gray-800")}
							>
								<span className="flex flex-1 items-center gap-2">
									{lang.name}
									{isEmpty && (
										<Tooltip label="Translation is empty">
											<span
												className="h-1.5 w-1.5 rounded-full bg-amber-500"
												aria-label="Translation is empty"
											/>
										</Tooltip>
									)}
								</span>
							</DropdownMenuItem>
						)
					})}
				</DropdownMenu>
			</Tooltip>
		)
	}

	return (
		<Tooltip
			label="Translation is empty"
			enabled={!!isSelectedEmpty}
			shouldWrapChildren
			side="right"
		>
			<Select
				ref={selectRef}
				name="language-selector"
				value={displayValue}
				onChange={handleSelect}
				options={options}
				placeholder={placeholder}
				disabled={disabled}
				Icon={Languages}
				iconClassName={cn("text-green-500", {
					"text-amber-500": isSelectedEmpty,
				})}
				selectClassName={cn(
					plainSelectStyle ? "w-fit" : buttonVariants({ variant: "outline" }),
					"focus-visible:!ring-1 focus-visible:!ring-black/15 dark:focus-visible:!ring-white/10",
				)}
				chevronClassName="h-3 w-3"
			/>
		</Tooltip>
	)
}
